import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type KeyboardEvent,
} from 'react';
import { ChevronDownIcon } from '@/shared/ui/Icons';

export interface SearchableOption {
  id: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onSelect: (id: string, label: string) => void;
  fetchOptions: (search: string) => Promise<SearchableOption[]>;
  placeholder?: string;
  selectedLabel?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  /** Debounce ms before fetching (default 250) */
  debounceMs?: number;
}

export function SearchableSelect({
  value,
  onSelect,
  fetchOptions,
  placeholder = 'Search...',
  selectedLabel,
  clearable = true,
  disabled = false,
  className = '',
  debounceMs = 250,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<SearchableOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const loadOptions = useCallback(
    async (term: string) => {
      setIsLoading(true);
      try {
        const data = await fetchOptions(term);
        setOptions(data);
        setHighlightIndex(-1);
      } catch {
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchOptions]
  );

  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadOptions(search);
    }, debounceMs);
    return () => clearTimeout(debounceRef.current);
  }, [search, isOpen, debounceMs, loadOptions]);

  useEffect(() => {
    if (isOpen) loadOptions(search);
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (opt: SearchableOption) => {
    onSelect(opt.id, opt.label);
    setSearch('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect('', '');
    setSearch('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIndex(-1);
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      setHighlightIndex((i) => (i < options.length - 1 ? i + 1 : i));
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowUp') {
      setHighlightIndex((i) => (i > 0 ? i - 1 : -1));
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' && highlightIndex >= 0 && options[highlightIndex]) {
      handleSelect(options[highlightIndex]);
      e.preventDefault();
    }
  };

  return (
    <div ref={containerRef} className={`relative min-w-[11rem] ${className}`}>
      <div
        className={`flex items-center rounded-lg border bg-slate-50/50 text-sm transition-colors ${
          disabled
            ? 'cursor-not-allowed border-slate-200 bg-slate-100 opacity-60'
            : 'cursor-text border-slate-200 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500'
        }`}
      >
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? search : (value && selectedLabel ? selectedLabel : '')}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={value ? undefined : placeholder}
          disabled={disabled}
          className="flex-1 min-w-0 rounded-lg border-0 bg-transparent px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
        />
        <div className="flex shrink-0 items-center gap-1 pr-2">
          {clearable && value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
              aria-label="Clear"
            >
              ×
            </button>
          )}
          <ChevronDownIcon
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {isLoading ? (
            <div className="px-3 py-4 text-center text-sm text-slate-500">Loading...</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-slate-500">
              {search ? 'No results' : 'Type to search'}
            </div>
          ) : (
            options.map((opt, i) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleSelect(opt)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                  i === highlightIndex ? 'bg-indigo-50 text-indigo-700' : 'text-slate-900'
                } ${opt.id === value ? 'font-medium' : ''}`}
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
